import { useResource } from "./useResource.ts"

export interface UniformBlock {
  /**
   * Fill the buffer with data if the data has changed. Then (regardless of whether the data changed) bind the buffer to the uniform block, run the body, and then unbind the block.
   * @param data 
   * @param body 
   */
  use(data: AllowSharedBufferSource, body: () => void): void

  /**
   * Size of the uniform block, in bytes.
   */
  byteSize: number
}

export function useUniformBlock(
  name: string,
  gl: WebGL2RenderingContext,
  program: WebGLProgram,
  usage: GLenum = gl.DYNAMIC_DRAW
): UniformBlock {
  return useResource(function* () {
    const index = gl.getUniformBlockIndex(program, name)

    // Each program has its own namespace of uniform block binding numbers. Each uniform block needs to be assigned to a unique binding number within that namespace. Since each uniform block is assigned a unique index by GL, I just use the index as the binding number and assume all other uniform block bindings are assigned the same way.
    gl.uniformBlockBinding(
      program,
      index, // uniform block index assigned by GL
      index // binding number assigned by me
    )

    const bo = gl.createBuffer()
    gl.getExtension('GMAN_debug_helper')?.tagObject(bo, `buffer for uniform block ${name}`)

    let priorData: AllowSharedBufferSource | null = null

    const byteSize = gl.getActiveUniformBlockParameter(program, index, gl.UNIFORM_BLOCK_DATA_SIZE)

    function use(data: AllowSharedBufferSource, body: () => void) {
      gl.bindBufferBase(gl.UNIFORM_BUFFER, index, bo)

      if (priorData !== data) {
        gl.bufferData(gl.UNIFORM_BUFFER, data, usage)
        priorData = data
      }

      body()

      gl.bindBufferBase(gl.UNIFORM_BUFFER, index, null)
    }

    yield { use, byteSize }

    gl.deleteBuffer(bo)
  }, [gl, name, program, usage])
}
